import React from 'react';
import { Skeleton } from '../../../tailwind/atoms';
import { bucketCount, fmtCount, fmtPct, shareAtLeast } from '../format';

const Stat = ({ label, value, valueClassName = 'text-gray-900' }) => (
  <div>
    <div className='text-ts-2xs font-semibold uppercase tracking-[0.10em] text-gray-600'>{label}</div>
    <div
      className={`mt-[5px] font-jetbrains-mono text-[17px] font-semibold tabular-nums ${valueClassName}`}
    >
      {value}
    </div>
  </div>
);

const Divider = () => <div className='w-px bg-gray-300' />;

const Pending = () => <Skeleton className='mt-[7px] h-3 w-16' />;

/**
 * Opening spread: the claim, then the four numbers that qualify it.
 *
 * Two of those numbers describe chapter 02, which is fetched lazily — so they render as placeholders
 * until that request lands rather than holding up the whole page for them.
 */
const ReportHero = ({ orgName, metabolic, deficiency }) => {
  const metabolicBuckets = metabolic.buckets;
  const anyAbnormality = shareAtLeast(metabolicBuckets, 1);
  const syndromeCount = bucketCount(metabolicBuckets, 3);
  const deficiencyTwoPlus = deficiency ? shareAtLeast(deficiency.buckets, 2) : null;

  return (
    <section className='mb-ds-2'>
      {/* Not the shared `.kicker--rule`: that helper draws a vertical 1x16px bar with an 8px gap, where
          the design calls for a horizontal 18x1px rule and a 10px gap. */}
      <div className='inline-flex items-center gap-[10px] text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-brand'>
        <span className='h-px w-[18px] bg-current' />
        {`Workforce health analysis · ${orgName}`}
      </div>

      <h1
        className='mb-ds-4 mt-[18px] max-w-[900px] font-fig-extrabold text-[52px] font-bold leading-[1.06] tracking-[-0.025em] text-gray-900'
        style={{ textWrap: 'pretty' }}
      >
        Two overlapping risk pictures in one workforce
      </h1>

      <p
        className='m-0 max-w-[760px] text-[19px] leading-[1.65] text-gray-700'
        style={{ textWrap: 'pretty' }}
      >
        Annual health-check records read through two lenses.{' '}
        <strong className='font-semibold text-gray-900'>{fmtPct(anyAbnormality)}</strong> of employees carry
        at least one metabolic abnormality. Separately,{' '}
        {deficiencyTwoPlus === null ? (
          <span className='inline-block align-middle'>
            <Skeleton className='h-3 w-10' />
          </span>
        ) : (
          <strong className='font-semibold text-gray-900'>{fmtPct(deficiencyTwoPlus)}</strong>
        )}{' '}
        carry two or more overlapping nutritional deficiencies. Neither group is small, and they overlap.
      </p>

      <div className='mt-[28px] flex flex-wrap gap-[28px] border-y border-gray-300 py-[18px]'>
        <Stat label='Metabolic cohort' value={fmtCount(metabolic.cohort.total)} />
        <Divider />
        <Stat
          label='Deficiency cohort'
          value={deficiency ? fmtCount(deficiency.cohort.total) : <Pending />}
        />
        <Divider />
        <Stat
          label='Markers screened'
          value={`${metabolic.cohort.markers} + ${deficiency ? deficiency.cohort.markers : '4'}`}
        />
        <Divider />
        <Stat
          label='Meets syndrome criteria today'
          value={fmtCount(syndromeCount)}
          valueClassName='text-ds-rose-dk'
        />
      </div>
    </section>
  );
};

export default ReportHero;
